import type { NeoMailboxProvisionResult, TenantRow } from "@/api";

/**
 * How registration sequences "create the business" and "give it a mailbox" (#710).
 *
 * Provisioning reaches an external mail server and can fail on its own, so the two steps cannot
 * be one atomic act. The issue asks which way that resolves, and this is the answer:
 *
 * **The business survives; the mailbox is retried.** Creating a tenant also creates its
 * immutable policy chain, so undoing it is not a delete — it would leave an archived shell or
 * require tearing down policy history to make a mail server hiccup disappear. A business
 * without a mailbox, on the other hand, is an ordinary state the product already supports:
 * every business created before this feature existed was in it, and the Mailboxes section is
 * the place to finish the job.
 *
 * What must never happen is the operator not being told. `created_without_mailbox` carries the
 * reason so the dialog can say what failed and where to continue, rather than reporting success
 * and leaving a business whose mail silently never arrives.
 */

export type MailboxChoice = "neo_hosted" | "bring_your_own";

export type RegistrationOutcome =
  /** Bring-your-own: exactly what registration did before this feature existed. */
  | { readonly kind: "created"; readonly tenant: TenantRow }
  | { readonly kind: "created_with_mailbox"; readonly tenant: TenantRow; readonly address: string }
  /** The business exists and is usable; the mailbox step is the only thing left to redo. */
  | {
      readonly kind: "created_without_mailbox";
      readonly tenant: TenantRow;
      readonly error: string;
    };

export interface RegistrationDeps {
  createTenant(input: { slug: string; displayName: string; prepared?: boolean }): Promise<TenantRow>;
  provisionNeoMailbox(tenantId: string, localPart: string): Promise<NeoMailboxProvisionResult>;
}

export interface RegistrationInput {
  slug: string;
  displayName: string;
  choice: MailboxChoice;
  /** Only read when `choice` is `neo_hosted`. */
  localPart?: string;
  /**
   * #780: create it suspended, for somebody else to finish. No mailbox step follows — the address
   * belongs to the person who will run the business, and they choose it when they accept.
   */
  prepared?: boolean;
}

/**
 * Creating the business is allowed to throw: nothing exists yet, so the caller's ordinary error
 * path is correct and there is nothing to clean up. Everything after it resolves to an outcome
 * instead, because by then something does exist and the caller has to be told what.
 */
export async function registerBusiness(
  deps: RegistrationDeps,
  input: RegistrationInput,
): Promise<RegistrationOutcome> {
  const tenant = await deps.createTenant({
    slug: input.slug.trim(),
    displayName: input.displayName.trim(),
    prepared: input.prepared === true,
  });

  // A prepared business has no address step here by construction: the invited owner picks it.
  if (input.prepared === true || input.choice !== "neo_hosted") return { kind: "created", tenant };

  const localPart = (input.localPart ?? "").trim();
  if (!localPart) {
    // Reachable only through a caller that ignored its own validation; treated as a failed
    // mailbox step rather than a thrown error, because the business is already there.
    return { kind: "created_without_mailbox", tenant, error: "no mailbox name was given" };
  }

  try {
    const provisioned = await deps.provisionNeoMailbox(tenant.id, localPart);
    return { kind: "created_with_mailbox", tenant, address: provisioned.emailAddress };
  } catch (cause) {
    return {
      kind: "created_without_mailbox",
      tenant,
      error: errorText(cause),
    };
  }
}

function errorText(cause: unknown): string {
  if (cause instanceof Error && cause.message) return cause.message;
  if (typeof cause === "string" && cause) return cause;
  return "unknown error";
}
